import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";

import { useLogin, useRegister } from "../api/queries.ts";

/**
 * 登录 / 注册的全部业务逻辑。
 *
 * 🔴 **注册时的 `inviteCode` 决定这个用户永久归属哪个分销商** ——
 *    从专属链接进来的人，链接里的码要一路带到这里，中途丢了就变成无归属用户，
 *    分销商辛苦拉的人白拉了。
 *
 * 🔴 手机版和网页版是两套版面，逻辑只能有一份 —— 人机验证票的一次性、
 *    冷却倒计时这两处都出过 bug，抄两份等于把 bug 也抄一份。
 */
export function useAuthForm() {
  const search = useSearch({ from: "/login" }) as {
    redirect?: string;
    invite?: string;
    visitId?: number;
  };
  const navigate = useNavigate();
  const login = useLogin();
  const register = useRegister();

  const [mode, setMode] = useState<"login" | "register">(search.invite ? "register" : "login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [nickname, setNickname] = useState("");
  /**
   * 确认密码（只在注册用）。老板 2026-08-31 指定。
   *
   * 🔴 注册时密码框是**遮住的**，打错一个字自己看不见 —— 而这个平台上
   *    账号是有钱的（余额、待结算），密码打错了他当场就进不去，
   *    只能走客服重置。多打一遍是最便宜的一道保险。
   */
  const [password2, setPassword2] = useState("");

  /**
   * 🔴 人机验证的解。
   *    注册**必须**带（刷号 = 刷归属关系，是这个平台上直接的经济损失）；
   *    登录平时不用带，只有服务端判定这个 IP 像在撞库时才回 CAPTCHA_REQUIRED，
   *    那时前端才补一道验证重试。
   */
  const [captcha, setCaptcha] = useState("");
  const [needCaptcha, setNeedCaptcha] = useState(false);

  /**
   * 🔴 **注册不再出人机验证**（老板 2026-08-31：「注册账号取消安全校验」）。
   *    留下的只有**登录侧**那条：服务端判定这个 IP 像在撞库时回 `CAPTCHA_REQUIRED`，
   *    那时才补一道验证重试 —— 那是服务端主动要的，不是常态。
   */
  const showCaptcha = needCaptcha;
  const busy = login.isPending || register.isPending;

  /**
   * 🔴 **没填就别发请求，把话说准。**
   *    服务端为了防账号枚举，登录失败一律回「账号或密码错误」
   *    （文案统一 + 时间恒定，这是刻意的）—— 密码填错时它是对的，
   *    但用户**一个字都没填**时它是误导：他会以为自己记错了密码、
   *    去点"忘记密码"，而真正的问题只是空着没填。
   *
   * ⚠️ 装成 `Error` 再抛给页面，因为两套版面渲染的都是
   *    `error instanceof Error ? error.message : "出了点问题"` ——
   *    直接给字符串的话，用户看到的会是那句没用的"出了点问题"。
   */
  const [formError, setFormError] = useState<Error | null>(null);
  const error = formError ?? login.error ?? register.error;

  /** ⚠️ 一动手就把这条提示撤掉 —— 他已经在改了，红字还挂着只会让人以为没生效。 */
  useEffect(() => {
    setFormError(null);
  }, [phone, password, password2, mode]);

  /**
   * 「请 N 秒后再试」的倒计时。
   *
   * 🔴 **数字必须真的在走。** 停在 30 不动的话用户会以为界面卡死了，
   *    然后不停点登录 —— 而每点一次，服务端的递进等待还会把时间**拉得更长**。
   *
   * ⚠️ 秒数取自 `details.retryAfterSec`，不是去解析那句文案 ——
   *    从中文里抠数字，改一个字就坏，而且不同语言完全不成立。
   */
  const retryAfter = (error as { details?: { retryAfterSec?: number } } | null)?.details
    ?.retryAfterSec;
  const [left, setLeft] = useState(0);

  useEffect(() => {
    if (!retryAfter) return;
    setLeft(retryAfter);
    const t = setInterval(() => {
      setLeft((n) => {
        if (n <= 1) {
          clearInterval(t);
          return 0;
        }
        return n - 1;
      });
    }, 1000);
    /** ⚠️ 卸载和换错误时都要清 —— 不清会留下越来越多的计时器一起改同一个数。 */
    return () => clearInterval(t);
  }, [retryAfter, error]);

  /** 冷却没走完就别让他点，点了只会把等待时间拉长。 */
  const cooling = left > 0;


  const submit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    /**
     * ⚠️ **密码不 trim 判空**：密码允许含空格，`trim()` 之后判空
     *    会把一个合法（虽然古怪）的密码当成没填。手机号可以 trim ——
     *    从别处复制过来常带一个尾随空格。
     */
    if (mode === "register" && password !== password2) {
      /** ⚠️ 这句跟登录那两句不冲突：它是注册独有的一件事，不说的话他不知道哪儿不对。 */
      setFormError(new Error("两次输入的密码不一致"));
      return;
    }
    if (!phone.trim() || password.length === 0) {
      /**
       * 🔴 **登录页的提示只有两种**（老板定的）：
       *    ① 没填 →「请输入账号和密码」  ② 填了但不对 →「账号或密码错误」。
       *    我一度按缺了哪一样分成三句（缺手机号 / 缺密码 / 都缺），
       *    被打回来了 —— 少一个输入框是空的这件事他自己看得见，
       *    多两种说法只是让同一件事有三个面孔。
       */
      setFormError(new Error("请输入账号和密码"));
      return;
    }
    setFormError(null);
    try {
      if (mode === "login") {
        await login.mutateAsync({
          identifier: phone,
          password,
          ...(captcha ? { captchaPayload: captcha } : {}),
        });
      } else {
        await register.mutateAsync({
          phone,
          password,
          nickname: nickname || undefined,
          inviteCode: search.invite,
          visitId: search.visitId,
          ...(captcha ? { captchaPayload: captcha } : {}),
        });
      }
    } catch (err) {
      /**
       * 撞库信号触发 → 让用户过一道人机验证再来。
       * ⚠️ 票是一次性的，重试前要把旧的清掉，否则第二次必然失败，
       *    表现成"验证明明过了却还提示请先完成人机验证"。
       */
      if ((err as { code?: string })?.code === "CAPTCHA_REQUIRED") {
        setCaptcha("");
        setNeedCaptcha(true);
      }
      return;
    }
    navigate({ to: search.redirect ?? "/" });
  };

  return {
    search,
    mode,
    setMode,
    phone,
    setPhone,
    password,
    setPassword,
    password2,
    setPassword2,
    nickname,
    setNickname,
    setCaptcha,
    showCaptcha,
    needCaptcha,
    busy,
    error,
    left,
    cooling,
    submit,
  };
}
